import React from 'react';
import { Tooltip, Popconfirm } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { User } from 'lucide-react';
import './styles.scss';

interface AccountSortItem {
  idAccount: number;
  accountId: string;
  initial_balance: number;
  current_balance_formatted: string;
  current_equity_formatted: string;
  current_pnl_formatted: string;
  todays_profit_formatted: string;
  current_equity: number;
  current_balance: number;
  current_pnl: number;
  todays_profit: number;
  created_at: string;
  status: 'active' | 'inactive' | 'suspended';
  platform: string;
  user: {
    userId: string;
    userName: string;
    broker: string;
  };
}

interface AccountSortViewProps {
  accounts: AccountSortItem[];
  onEdit?: (account: AccountSortItem) => void;
  onDelete: (id: number, platform: string, account_number: string) => void;
}

const AccountSortView: React.FC<AccountSortViewProps> = ({ accounts, onEdit, onDelete }) => {
  const { t } = useTranslation();

  return (
    <div className="account-sort-view space-y-3">
        {accounts.map((account) => (
          <div
            key={account.idAccount}
          className="account-sort-row flex flex-col md:flex-row items-start md:items-center justify-between gap-3 rounded-xl px-4 md:px-6 py-3 md:py-4 shadow-sm transition-colors"
          style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}
          >
            {/* Account info */}
            <div className="flex items-center gap-3 min-w-[240px]">
              <div className="flex h-10 w-10 items-center justify-center rounded-full" style={{ color: 'var(--text-desc)', background: 'var(--bg-sub)' }}>
                <User className="w-5 h-5" />
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold" style={{ color: 'var(--text-title)' }}>
                    {account.user?.userName || 'Trader'}
                  </span>
                  <span className="rounded-full px-2 py-0.5 text-[11px] font-medium" style={{ background: 'var(--stat-teal-bg)', color: 'var(--stat-teal-text)', border: '1px solid var(--stat-teal-text)' }}>
                    #{account.platform?.toUpperCase() || 'MT5'}
                  </span>
                </div>
                <span className="text-sm mt-0.5" style={{ color: 'var(--text-desc)' }}>{account.accountId}</span>
              </div>
            </div>

            {/* Metrics block */}
            <div className="flex-1 grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-3 text-right md:ml-4">
              <div className="flex flex-col items-end">
                <span className="text-sm" style={{ color: 'var(--text-desc)' }}>{t('Balance')}</span>
                <span className="text-sm font-semibold" style={{ color: 'var(--text-title)' }}>
                  {account.current_balance_formatted}
                </span>
              </div>

              {/* Today profit - main sort metric, highlight */}
              <div className="flex flex-col items-end">
                <span className="text-sm" style={{ color: 'var(--text-desc)' }}>{t('Todayprofit')}</span>
                <span
                  className="text-sm font-semibold"
                  style={{
                    color: account.todays_profit > 0
                      ? 'var(--stat-green-text)'
                      : account.todays_profit < 0
                      ? 'var(--stat-red-text)'
                      : 'var(--stat-yellow-text)'
                  }}
                >
                  {account.todays_profit_formatted || '$0.00'}
                </span>
              </div>

              <div className="flex flex-col items-end">
                <span className="text-sm" style={{ color: 'var(--text-desc)' }}>{t('Equity')}</span>
                <span className="text-sm font-semibold" style={{ color: 'var(--stat-teal-text)' }}>
                  {account.current_equity_formatted || '$0.00'}
                </span>
              </div>

              <div className="flex flex-col items-end">
                <span className="text-sm" style={{ color: 'var(--text-desc)' }}>{t('UnrealizedPnL')}</span>
                <span
                  className="text-sm font-semibold"
                  style={{
                    color: account.current_pnl > 0
                      ? 'var(--stat-green-text)'
                      : account.current_pnl < 0
                      ? 'var(--stat-red-text)'
                      : 'var(--text-title)'
                  }}
                >
                  {account.current_pnl_formatted || '$0.00'}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-start md:justify-center gap-2">
              <Tooltip title={t('EditAccount')}>
                <button
                  type="button"
                  className="flex h-8 w-8 items-center justify-center rounded-full border"
                  style={{ borderColor: 'var(--card-border)', color: 'var(--text-desc)', background: 'var(--card-bg)' }}
                  onClick={() => onEdit?.(account)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 256 256"
                    fill="currentColor"
                  >
                    <path d="M227.31,73.37,182.63,28.68a16,16,0,0,0-22.63,0L36.69,152A15.86,15.86,0,0,0,32,163.31V208a16,16,0,0,0,16,16H92.69A15.86,15.86,0,0,0,104,219.31L227.31,96a16,16,0,0,0,0-22.63ZM92.69,208H48V163.31l88-88L180.69,120ZM192,108.68,147.31,64l24-24L216,84.68Z" />
                  </svg>
                </button>
              </Tooltip>

              <Popconfirm
                title={t('DeleteAccount')}
                description={t('DeleteAccountConfirm')}
                onConfirm={() => onDelete(account.idAccount, account.platform, account.accountId)}
                okText="Yes"
                cancelText="No"
              >
                <Tooltip title={t('DeleteAccount')}>
                  <button
                    type="button"
                    className="flex h-8 w-8 items-center justify-center rounded-full border"
                    style={{ borderColor: 'var(--card-border)', color: 'var(--stat-red-text)', background: 'var(--card-bg)' }}
                  >
                    <DeleteOutlined />
                  </button>
                </Tooltip>
              </Popconfirm>
            </div>
          </div>
        ))}
      </div>
  );
};

export default AccountSortView;


